import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('servio_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  register: (data) => axios.post(`${API}/auth/register`, data),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),
  requestPasswordReset: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }),
  changePassword: (currentPassword, newPassword) => axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: getAuthHeader() }),

  // Servers
  searchServers: (params) => axios.get(`${API}/servers/search/results`, { params }),
  getServer: (id, locationParams = {}) => axios.get(`${API}/servers/${id}`, { params: locationParams }),
  getMyProfile: () => axios.get(`${API}/servers/profile/me`, { headers: getAuthHeader() }),
  createProfile: (data) => axios.post(`${API}/servers/profile`, data, { headers: getAuthHeader() }),
  updateProfile: (data) => axios.put(`${API}/servers/profile/me`, data, { headers: getAuthHeader() }),
  
  // Profile Photo
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/servers/profile/photo`, formData, { 
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: () => axios.delete(`${API}/servers/profile/photo`, { headers: getAuthHeader() }),
  
  // Profile Slug (short URL)
  generateSlug: () => axios.post(`${API}/servers/profile/generate-slug`, {}, { headers: getAuthHeader() }),
  getServerBySlug: (slug) => axios.get(`${API}/servers/by-slug/${slug}`),

  // State Licensing Requirements
  getAllStateRequirements: () => axios.get(`${API}/state-requirements`),
  getStateRequirement: (state) => axios.get(`${API}/state-requirements/${state}`),
  getMyStateCertifications: () => axios.get(`${API}/servers/state-certifications`, { headers: getAuthHeader() }),
  canAddCoverageInState: (state, county = null) => {
    const params = county ? { county } : {};
    return axios.get(`${API}/servers/can-add-coverage/${state}`, { headers: getAuthHeader(), params });
  },

  // Verification Documents (updated to support state certifications)
  uploadDocument: (file, documentType, expirationDate, licenseNumber, issuingAuthority, notes, stateCode = null, county = null, attestationAccepted = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({
      document_type: documentType,
      expiration_date: expirationDate,
      ...(licenseNumber && { certification_number: licenseNumber }),
      ...(issuingAuthority && { issuing_authority: issuingAuthority }),
      ...(notes && { notes: notes }),
      ...(stateCode && { state_code: stateCode }),
      ...(county && { county: county }),
      ...(attestationAccepted !== null && { attestation_accepted: attestationAccepted })
    });
    return axios.post(`${API}/documents/upload?${params}`, formData, { 
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Identity verification status
  getIdentityStatus: () => axios.get(`${API}/servers/identity-status`, { headers: getAuthHeader() }),
  canAddCoverage: (state) => axios.get(`${API}/servers/can-add-coverage/${state}`, { headers: getAuthHeader() }),
  getSubscriptionLimits: () => axios.get(`${API}/servers/subscription-limits`, { headers: getAuthHeader() }),
  
  // Downgrade handling
  getDowngradeStatus: () => axios.get(`${API}/servers/downgrade-status`, { headers: getAuthHeader() }),
  completeDowngrade: (selectedAreaIds) => axios.post(`${API}/servers/complete-downgrade`, { selected_area_ids: selectedAreaIds }, { headers: getAuthHeader() }),
  restoreCoverage: () => axios.post(`${API}/servers/restore-coverage`, {}, { headers: getAuthHeader() }),
  
  getMyDocuments: () => axios.get(`${API}/documents/my`, { headers: getAuthHeader() }),
  getDocument: (docId) => axios.get(`${API}/documents/${docId}`, { headers: getAuthHeader() }),
  deleteDocument: (docId) => axios.delete(`${API}/documents/${docId}`, { headers: getAuthHeader() }),

  // Notifications
  getNotifications: (unreadOnly = false) => axios.get(`${API}/notifications`, { 
    headers: getAuthHeader(),
    params: { unread_only: unreadOnly }
  }),
  markNotificationRead: (notificationId) => axios.post(`${API}/notifications/${notificationId}/read`, {}, { headers: getAuthHeader() }),
  markAllNotificationsRead: () => axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() }),

  // Quotes
  requestQuote: (data) => axios.post(`${API}/quotes/request`, data),
  calculateMileage: (serverId, serviceAddress) => axios.post(`${API}/servers/${serverId}/calculate-mileage`, {
    server_id: serverId,
    service_street: serviceAddress.street,
    service_city: serviceAddress.city,
    service_state: serviceAddress.state,
    service_zip: serviceAddress.zip,
    // Optional: verified coordinates from autocomplete
    service_lat: serviceAddress.lat || null,
    service_lng: serviceAddress.lng || null,
    verified: serviceAddress.verified || false
  }),
  checkCoverage: (serverId, addressData) => axios.post(`${API}/servers/${serverId}/check-coverage`, {
    service_city: addressData.city,
    service_state: addressData.state,
    service_zip: addressData.zip,
    service_county: addressData.county || null
  }),
  updateQuoteSettings: (settings) => axios.put(`${API}/servers/profile/quote-settings`, settings, { headers: getAuthHeader() }),
  getReceivedQuotes: () => axios.get(`${API}/quotes/received`, { headers: getAuthHeader() }),
  respondToQuote: (quoteId, data) => axios.post(`${API}/quotes/${quoteId}/respond`, data, { headers: getAuthHeader() }),
  declineQuote: (quoteId, message) => axios.post(`${API}/quotes/${quoteId}/decline`, null, { 
    headers: getAuthHeader(),
    params: message ? { message } : {}
  }),
  markQuoteComplete: (quoteId) => axios.post(`${API}/quotes/${quoteId}/complete`, {}, { headers: getAuthHeader() }),

  // Client Quote Flow (no auth required)
  getQuoteForClient: (clientToken) => axios.get(`${API}/quotes/client/${clientToken}`),
  acceptQuoteByToken: (clientToken) => axios.post(`${API}/quotes/client/${clientToken}/accept`),
  broadcastQuote: (data) => axios.post(`${API}/quotes/broadcast`, data),

  // Additional Services
  getAvailableServices: () => axios.get(`${API}/services/available`),
  getStateRequirements: (state) => axios.get(`${API}/services/requirements/${state}`),
  getMyServices: () => axios.get(`${API}/services/my`, { headers: getAuthHeader() }),
  updateServices: (services) => axios.put(`${API}/services/update`, services, { headers: getAuthHeader() }),

  // Reviews
  getReviewTokenInfo: (token) => axios.get(`${API}/reviews/token/${token}`),
  createReview: (data) => axios.post(`${API}/reviews`, data),
  getServerReviews: (serverId, params) => axios.get(`${API}/reviews/server/${serverId}`, { params }),

  // Subscriptions
  createCheckout: (planType) => axios.post(`${API}/subscriptions/checkout`, {
    plan_type: planType,
    origin_url: window.location.origin
  }, { headers: getAuthHeader() }),
  getCheckoutStatus: (sessionId) => axios.get(`${API}/subscriptions/status/${sessionId}`, { headers: getAuthHeader() }),
  getFoundingCount: () => axios.get(`${API}/subscriptions/founding-count`),
  getDirectoryStats: () => axios.get(`${API}/public/directory-stats`),
  createBillingPortal: () => axios.post(`${API}/subscriptions/billing-portal`, {}, { headers: getAuthHeader() }),

  // Analytics
  getDashboard: () => axios.get(`${API}/analytics/dashboard`, { headers: getAuthHeader() }),

  // Admin
  getAdminStats: () => axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
  getPendingDocuments: (documentType) => axios.get(`${API}/admin/documents/pending`, { 
    headers: getAuthHeader(),
    params: documentType ? { document_type: documentType } : {}
  }),
  getAllDocuments: (params) => axios.get(`${API}/admin/documents/all`, { headers: getAuthHeader(), params }),
  getAdminDocument: (docId) => axios.get(`${API}/admin/documents/${docId}`, { headers: getAuthHeader() }),
  verifyDocument: (docId, status, adminNotes) => axios.post(`${API}/admin/documents/${docId}/verify`, {
    status,
    admin_notes: adminNotes
  }, { headers: getAuthHeader() }),
  getAdminUsers: (params) => axios.get(`${API}/admin/users`, { headers: getAuthHeader(), params }),
  getAdminServers: (params) => axios.get(`${API}/admin/servers`, { headers: getAuthHeader(), params }),
  getServerCredentials: (serverId) => axios.get(`${API}/admin/servers/${serverId}/credentials`, { headers: getAuthHeader() }),
  updateUserRole: (userId, role) => axios.put(`${API}/admin/users/${userId}/role`, { role }, { headers: getAuthHeader() }),
  triggerExpirationCheck: () => axios.post(`${API}/admin/check-expirations`, {}, { headers: getAuthHeader() }),

  // Utility
  getStates: () => axios.get(`${API}/states`),
  lookupZip: (zipCode) => axios.get(`${API}/zip-lookup/${zipCode}`),

  // Complaints
  getComplaintCategories: () => axios.get(`${API}/complaints/categories`),
  submitComplaint: (data) => axios.post(`${API}/complaints/submit`, data, { headers: getAuthHeader() }),
  uploadComplaintEvidence: (complaintId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/complaints/${complaintId}/evidence`, formData, { 
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteComplaintEvidence: (complaintId, fileId) => axios.delete(`${API}/complaints/${complaintId}/evidence/${fileId}`, { headers: getAuthHeader() }),
  getMyComplaints: () => axios.get(`${API}/complaints/my`, { headers: getAuthHeader() }),
  getComplaintsAgainstMe: () => axios.get(`${API}/complaints/against-me`, { headers: getAuthHeader() }),
  getComplaintDetails: (complaintId) => axios.get(`${API}/complaints/${complaintId}`, { headers: getAuthHeader() }),
  getComplaintEvidenceFile: (complaintId, fileId) => axios.get(`${API}/complaints/${complaintId}/evidence/${fileId}`, { headers: getAuthHeader() }),
  respondToComplaint: (complaintId, responseText) => axios.post(`${API}/complaints/${complaintId}/respond`, { response_text: responseText }, { headers: getAuthHeader() }),
  
  // Admin Complaints
  getAdminComplaints: (params) => axios.get(`${API}/admin/complaints`, { headers: getAuthHeader(), params }),
  getAdminComplaintDetails: (complaintId) => axios.get(`${API}/admin/complaints/${complaintId}`, { headers: getAuthHeader() }),
  adminComplaintAction: (complaintId, data) => axios.post(`${API}/admin/complaints/${complaintId}/action`, data, { headers: getAuthHeader() }),
  getComplaintStats: () => axios.get(`${API}/admin/complaints/stats`, { headers: getAuthHeader() }),
  
  // Contact
  submitContactForm: (data) => axios.post(`${API}/contact`, data),
  
  // Admin - Founding Member
  updateFoundingMemberStatus: (serverId, grant) => axios.post(`${API}/admin/servers/${serverId}/founding-member`, { grant }, { headers: getAuthHeader() }),
  
  // Admin - Send Profile Reminder
  sendProfileReminder: (serverId) => axios.post(`${API}/admin/servers/${serverId}/send-reminder`, {}, { headers: getAuthHeader() }),
  
  // Admin - Refresh Badges
  refreshServerBadges: (serverId) => axios.post(`${API}/admin/servers/${serverId}/refresh-badges`, {}, { headers: getAuthHeader() }),
  
  // Admin - Bulk Actions
  bulkRefreshBadges: () => axios.post(`${API}/admin/servers/bulk-refresh-badges`, {}, { headers: getAuthHeader() }),
  bulkFixListed: () => axios.post(`${API}/admin/servers/bulk-fix-listed`, {}, { headers: getAuthHeader() }),
  
  // Admin - Change Document Type
  changeDocumentType: (docId, documentType) => axios.post(`${API}/admin/documents/${docId}/change-type`, { document_type: documentType }, { headers: getAuthHeader() }),
  
  // Route Optimizer
  createRoute: (data) => axios.post(`${API}/routes`, data, { headers: getAuthHeader() }),
  getRoutes: (params) => axios.get(`${API}/routes`, { headers: getAuthHeader(), params }),
  getRoute: (routeId) => axios.get(`${API}/routes/${routeId}`, { headers: getAuthHeader() }),
  updateRoute: (routeId, data) => axios.put(`${API}/routes/${routeId}`, data, { headers: getAuthHeader() }),
  deleteRoute: (routeId) => axios.delete(`${API}/routes/${routeId}`, { headers: getAuthHeader() }),
  optimizeRoute: (routeId, currentLat, currentLng, currentTime) => axios.post(`${API}/routes/${routeId}/optimize`, null, { 
    headers: getAuthHeader(), 
    params: { current_lat: currentLat, current_lng: currentLng, current_time: currentTime } 
  }),
  startRoute: (routeId, currentTime = null) => {
    // Start route - commits to execution mode
    const now = new Date();
    const localTime = currentTime || `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    return axios.post(`${API}/routes/${routeId}/start`, null, {
      headers: getAuthHeader(),
      params: { current_time: localTime }
    });
  },
  getRouteDirections: (routeId) => axios.post(`${API}/routes/${routeId}/directions`, null, { headers: getAuthHeader() }),
  calculateArrivals: (routeId, startTime = '08:00') => axios.post(`${API}/routes/${routeId}/calculate-arrivals`, null, {
    headers: getAuthHeader(),
    params: { start_time: startTime }
  }),
  completeStop: (routeId, stopId, status = 'completed', currentTime = null, actualLat = null, actualLng = null) => {
    // Send current local time for ETA recalculation
    const now = new Date();
    const localTime = currentTime || `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const params = { status, current_time: localTime };
    
    // Add actual GPS coordinates for mileage tracking if provided
    if (actualLat !== null && actualLng !== null) {
      params.actual_lat = actualLat;
      params.actual_lng = actualLng;
    }
    
    return axios.post(`${API}/routes/${routeId}/stops/${stopId}/complete`, null, {
      headers: getAuthHeader(),
      params
    });
  },
  resetRoute: (routeId) => axios.post(`${API}/routes/${routeId}/reset`, null, { headers: getAuthHeader() }),
  archiveRoute: (routeId) => axios.post(`${API}/routes/${routeId}/archive`, null, { headers: getAuthHeader() }),
  unarchiveRoute: (routeId) => axios.post(`${API}/routes/${routeId}/unarchive`, null, { headers: getAuthHeader() }),
  duplicateRoute: (routeId, payload = {}) => axios.post(`${API}/routes/${routeId}/duplicate`, payload, { headers: getAuthHeader() }),
  saveRouteAsTemplate: (routeId, templateName) => axios.post(`${API}/routes/${routeId}/save-as-template`, { template_name: templateName }, { headers: getAuthHeader() }),
  unmarkRouteTemplate: (routeId) => axios.post(`${API}/routes/${routeId}/unmark-template`, null, { headers: getAuthHeader() }),
  listRouteTemplates: () => axios.get(`${API}/routes/templates/list`, { headers: getAuthHeader() }),
  fixRouteCoordinates: (routeId) => axios.post(`${API}/routes/${routeId}/fix-coordinates`, null, { headers: getAuthHeader() }),
  geocodeAddress: (address) => axios.post(`${API}/routes/geocode`, null, { headers: getAuthHeader(), params: { address } }),
  getAddressSuggestions: (query) => axios.get(`${API}/routes/address-suggestions`, { headers: getAuthHeader(), params: { query } }),
  
  // PDF Export
  exportRoutePdf: (routeId) => axios.get(`${API}/routes/${routeId}/export-pdf`, { 
    headers: getAuthHeader(),
    responseType: 'blob'
  }),
  
  // Route Sharing
  createShareLink: (routeId) => axios.post(`${API}/routes/${routeId}/share`, null, { headers: getAuthHeader() }),
  revokeShareLink: (routeId) => axios.delete(`${API}/routes/${routeId}/share`, { headers: getAuthHeader() }),
  updateShareVisibility: (routeId, payload) => axios.put(`${API}/routes/${routeId}/share/visibility`, payload, { headers: getAuthHeader() }),
  getRoutePDFData: (routeId, includeDirections = false) => axios.get(`${API}/routes/${routeId}/pdf-data`, { params: { include_directions: includeDirections }, headers: getAuthHeader() }),
  getSharedRoute: (shareToken) => axios.get(`${API}/track/${shareToken}`),
  
  // Route Import from File
  importStopsFromFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/routes/import-file`, formData, { 
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } 
    });
  },
  downloadImportTemplate: () => axios.get(`${API}/routes/download-template`, { 
    headers: getAuthHeader(),
    responseType: 'blob'
  }),

  // Invoice System
  getInvoices: (params) => axios.get(`${API}/invoices`, { headers: getAuthHeader(), params }),
  getInvoice: (invoiceId) => axios.get(`${API}/invoices/${invoiceId}`, { headers: getAuthHeader() }),
  getInvoiceStats: () => axios.get(`${API}/invoices/stats`, { headers: getAuthHeader() }),
  createInvoice: (data) => axios.post(`${API}/invoices`, data, { headers: getAuthHeader() }),
  updateInvoice: (invoiceId, data) => axios.put(`${API}/invoices/${invoiceId}`, data, { headers: getAuthHeader() }),
  deleteInvoice: (invoiceId) => axios.delete(`${API}/invoices/${invoiceId}`, { headers: getAuthHeader() }),
  sendInvoice: (invoiceId, data) => axios.post(`${API}/invoices/${invoiceId}/send`, data, { headers: getAuthHeader() }),
  sendInvoiceReminder: (invoiceId, data) => axios.post(`${API}/invoices/${invoiceId}/reminder`, data, { headers: getAuthHeader() }),
  duplicateInvoice: (invoiceId) => axios.post(`${API}/invoices/${invoiceId}/duplicate`, null, { headers: getAuthHeader() }),
  recordPayment: (invoiceId, data) => axios.post(`${API}/invoices/${invoiceId}/payment`, data, { headers: getAuthHeader() }),
  updatePayment: (invoiceId, paymentId, data) => axios.put(`${API}/invoices/${invoiceId}/payment/${paymentId}`, data, { headers: getAuthHeader() }),
  
  // Invoice Clients/Contacts
  getInvoiceClients: () => axios.get(`${API}/invoices/clients`, { headers: getAuthHeader() }),
  getInvoiceClient: (clientId) => axios.get(`${API}/invoices/clients/${clientId}`, { headers: getAuthHeader() }),
  createInvoiceClient: (data) => axios.post(`${API}/invoices/clients`, data, { headers: getAuthHeader() }),
  updateInvoiceClient: (clientId, data) => axios.put(`${API}/invoices/clients/${clientId}`, data, { headers: getAuthHeader() }),
  deleteInvoiceClient: (clientId) => axios.delete(`${API}/invoices/clients/${clientId}`, { headers: getAuthHeader() }),
  
  // Invoice Companies
  getInvoiceCompanies: () => axios.get(`${API}/invoices/companies`, { headers: getAuthHeader() }),
  getInvoiceCompany: (companyId) => axios.get(`${API}/invoices/companies/${companyId}`, { headers: getAuthHeader() }),
  createInvoiceCompany: (data) => axios.post(`${API}/invoices/companies`, data, { headers: getAuthHeader() }),
  updateInvoiceCompany: (companyId, data) => axios.put(`${API}/invoices/companies/${companyId}`, data, { headers: getAuthHeader() }),
  deleteInvoiceCompany: (companyId, cascade = false) => axios.delete(`${API}/invoices/companies/${companyId}${cascade ? '?cascade=true' : ''}`, { headers: getAuthHeader() }),
  
  // Invoice Settings
  getInvoiceSettings: () => axios.get(`${API}/invoices/settings`, { headers: getAuthHeader() }),
  updateInvoiceSettings: (data) => axios.put(`${API}/invoices/settings`, data, { headers: getAuthHeader() }),

  // Stripe Connect
  createStripeConnectAccount: (returnUrl) => axios.post(`${API}/stripe-connect/onboard`, { return_url: returnUrl }, { headers: getAuthHeader() }),
  getStripeConnectStatus: () => axios.get(`${API}/stripe-connect/status`, { headers: getAuthHeader() }),
  refreshStripeConnectLink: (returnUrl) => axios.post(`${API}/stripe-connect/refresh-link`, { return_url: returnUrl }, { headers: getAuthHeader() }),
  getStripeDashboardLink: () => axios.get(`${API}/stripe-connect/dashboard-link`, { headers: getAuthHeader() }),

  // Public Invoice Payment
  createInvoiceCheckout: (invoiceId, originUrl) => axios.post(`${API}/public/invoice/${invoiceId}/checkout`, { origin_url: originUrl }),
  getInvoicePaymentStatus: (invoiceId, sessionId) => axios.get(`${API}/public/invoice/${invoiceId}/payment-status/${sessionId}`),

  // Statements
  getStatements: () => axios.get(`${API}/statements`, { headers: getAuthHeader() }),
  getStatement: (statementId) => axios.get(`${API}/statements/${statementId}`, { headers: getAuthHeader() }),
  createStatement: (data) => axios.post(`${API}/statements`, data, { headers: getAuthHeader() }),
  deleteStatement: (statementId) => axios.delete(`${API}/statements/${statementId}`, { headers: getAuthHeader() }),
  recordStatementPayment: (statementId, data) => axios.post(`${API}/statements/${statementId}/payment`, data, { headers: getAuthHeader() }),
  getClientStatements: (clientId) => axios.get(`${API}/statements/client/${clientId}`, { headers: getAuthHeader() }),
  sendStatement: (statementId, data) => axios.post(`${API}/statements/${statementId}/send`, data, { headers: getAuthHeader() }),
  
  // Public Statement
  getPublicStatement: (statementId) => axios.get(`${API}/public/statement/${statementId}`),
  createStatementCheckout: (statementId) => axios.post(`${API}/public/statement/${statementId}/create-checkout`),
  getStatementPaymentStatus: (statementId, sessionId) => axios.get(`${API}/public/statement/${statementId}/payment-status/${sessionId}`),

  // Route Analytics
  getRouteAnalytics: (routeId) => axios.get(`${API}/routes/${routeId}/analytics`, { headers: getAuthHeader() }),
  addStopToRoute: (routeId, stopData) => axios.post(`${API}/routes/${routeId}/add-stop`, stopData, { headers: getAuthHeader() }),
  
  // Lifetime Analytics
  getLifetimeAnalytics: () => axios.get(`${API}/analytics/lifetime`, { headers: getAuthHeader() }),
  
  // Vehicles
  getVehicles: () => axios.get(`${API}/vehicles`, { headers: getAuthHeader() }),
  createVehicle: (data) => axios.post(`${API}/vehicles`, null, { headers: getAuthHeader(), params: data }),
  updateVehicle: (vehicleId, data) => axios.put(`${API}/vehicles/${vehicleId}`, null, { headers: getAuthHeader(), params: data }),
  deleteVehicle: (vehicleId) => axios.delete(`${API}/vehicles/${vehicleId}`, { headers: getAuthHeader() }),
  setDefaultVehicle: (vehicleId) => axios.put(`${API}/vehicles/${vehicleId}/set-default`, null, { headers: getAuthHeader() }),
  updateMileageEntryVehicle: (entryId, vehicleId) => 
    axios.put(`${API}/mileage-entries/${entryId}/vehicle`, null, { headers: getAuthHeader(), params: { vehicle_id: vehicleId } }),
  
  // Mileage Log
  getMileageLog: (year, month) => {
    const params = {};
    if (year) params.year = year;
    if (month) params.month = month;
    return axios.get(`${API}/mileage-log`, { headers: getAuthHeader(), params });
  },
  setRouteVehicle: (routeId, vehicleId) => axios.put(`${API}/routes/${routeId}/vehicle`, null, { headers: getAuthHeader(), params: { vehicle_id: vehicleId } }),
  markMileageReviewed: (entryId, reviewed = true) => axios.put(`${API}/mileage-entries/${entryId}/reviewed`, null, { headers: getAuthHeader(), params: { reviewed } }),
  
  // Segment classification (business/personal/unmarked)
  updateSegmentClassification: (entryId, segmentId, isBusiness) => 
    axios.put(`${API}/mileage-entries/${entryId}/segments/${segmentId}`, null, { 
      headers: getAuthHeader(), 
      params: { is_business: isBusiness } 
    }),
  bulkUpdateSegments: (entryId, segmentIds, isBusiness) => {
    const queryParams = new URLSearchParams();
    segmentIds.forEach(id => queryParams.append('segment_ids', id));
    if (isBusiness !== null) queryParams.append('is_business', isBusiness);
    return axios.put(`${API}/mileage-entries/${entryId}/segments/bulk?${queryParams.toString()}`, null, { 
      headers: getAuthHeader()
    });
  },
  
  // Manual mileage entry
  createManualMileageEntry: (data) => axios.post(`${API}/mileage-entries/manual`, data, { headers: getAuthHeader() }),
  deleteMileageEntry: (entryId) => axios.delete(`${API}/mileage-entries/${entryId}`, { headers: getAuthHeader() }),
  
  // Auto-tracked trip (from mobile GPS)
  createAutoTrackedTrip: (data) => axios.post(`${API}/mileage-entries/auto-tracked`, data, { headers: getAuthHeader() }),
  
  // Vehicle odometer
  updateVehicleOdometer: (vehicleId, odometer) => 
    axios.put(`${API}/vehicles/${vehicleId}/odometer`, null, { headers: getAuthHeader(), params: { odometer_reading: odometer } }),
  syncVehicleOdometer: (vehicleId) =>
    axios.post(`${API}/vehicles/${vehicleId}/sync-odometer`, null, { headers: getAuthHeader() }),
  getVehicleStats: (vehicleId, year) => 
    axios.get(`${API}/vehicles/${vehicleId}/stats`, { headers: getAuthHeader(), params: year ? { year } : {} }),
  
  // Route polyline for maps
  getEntryPolyline: (entryId) => axios.get(`${API}/mileage-entries/${entryId}/polyline`, { headers: getAuthHeader() }),
  geocodeMileageEntry: (entryId) => axios.put(`${API}/mileage-entries/${entryId}/geocode`, null, { headers: getAuthHeader() }),
  
  exportMileageLog: (year, month) => {
    const params = { format: 'csv' };
    if (year) params.year = year;
    if (month) params.month = month;
    return axios.get(`${API}/mileage-log/export`, { headers: getAuthHeader(), params, responseType: 'blob' });
  },
};

export default api;
